import { useState, useEffect, useCallback, useMemo } from 'react';
import { logError } from '../lib/logError';
import {
    getSDRs,
    getHubSources,
    updateHubSourceSDRConfig,
    getHubSdrAssignmentsRecent,
    getHubSdrState,
} from '../services/api';

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

function buildSlots(sdrs) {
    const total = sdrs.reduce((s, r) => s + (Number(r.weight) || 0), 0);
    if (total === 0) return sdrs.map(s => ({ ...s }));
    const divisor = sdrs.reduce((g, r) => gcd(g, Number(r.weight) || 1), Number(sdrs[0].weight) || 1) || 1;
    return sdrs.flatMap(r => Array(Math.max(1, Math.round((Number(r.weight) || 1) / divisor))).fill(r));
}

const STRATEGY_LABEL = {
    round_robin: 'Round-robin',
    fallback: 'Fallback fonte',
    global_fallback: 'Fallback global',
};

function formatDateTime(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }); }
    catch { return iso; }
}

const sLabel = { fontSize: '0.7rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' };

const FilaRecebimento = ({ embedded = false }) => {
    const [sources, setSources] = useState([]);
    const [sdrs, setSdrs] = useState([]);
    const [selectedSourceId, setSelectedSourceId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [sdrEnabled, setSdrEnabled] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const [fallbackSdr, setFallbackSdr] = useState('');

    const [history, setHistory] = useState([]);
    const [queueState, setQueueState] = useState(null);
    const [obsLoading, setObsLoading] = useState(false);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [sourcesData, sdrsData] = await Promise.all([getHubSources(), getSDRs()]);
            setSources(sourcesData);
            setSdrs(sdrsData);
            if (sourcesData.length > 0 && !selectedSourceId) setSelectedSourceId(sourcesData[0].id);
        } catch (err) {
            await logError(err, { fn: 'FilaRecebimento/loadData' });
            setErrorMessage('Erro ao carregar dados. Tente recarregar a página.');
        } finally {
            setIsLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        if (!selectedSourceId) return;
        const source = sources.find(s => s.id === selectedSourceId);
        if (!source) return;
        const routing = source.ghl_routing ?? {};
        setSdrEnabled(routing.sdr_distribution_enabled ?? false);
        setAssignments(routing.sdr_assignments?.map(a => ({ ...a })) ?? []);
        setFallbackSdr(routing.fallback_sdr ?? '');
        setSuccessMessage('');
        setErrorMessage('');
    }, [selectedSourceId, sources]);

    useEffect(() => {
        if (!selectedSourceId) return;
        const source = sources.find(s => s.id === selectedSourceId);
        if (!source?.slug) return;
        let cancelled = false;
        (async () => {
            setObsLoading(true);
            try {
                const [hist, state] = await Promise.all([
                    getHubSdrAssignmentsRecent(source.slug, 30),
                    getHubSdrState(source.slug),
                ]);
                if (cancelled) return;
                setHistory(hist);
                setQueueState(state);
            } catch (err) {
                if (!cancelled) await logError(err, { fn: 'FilaRecebimento/loadObservability' });
            } finally {
                if (!cancelled) setObsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedSourceId, sources]);

    const addSdr = (sdrNome) => {
        if (!sdrNome || assignments.find(a => a.nome === sdrNome)) return;
        const sdr = sdrs.find(s => s.nome === sdrNome);
        setAssignments(prev => [...prev, { nome: sdrNome, ghl_user_id: sdr?.ghl_user_id || '', weight: 50, enabled: true }]);
    };
    const removeSdr = (nome) => setAssignments(prev => prev.filter(a => a.nome !== nome));
    const updateAssignment = (nome, field, value) =>
        setAssignments(prev => prev.map(a => a.nome === nome ? { ...a, [field]: value } : a));

    const totalWeight = assignments.filter(a => a.enabled).reduce((s, a) => s + (Number(a.weight) || 0), 0);

    const handleSave = async () => {
        setIsSaving(true);
        setSuccessMessage('');
        setErrorMessage('');
        try {
            await updateHubSourceSDRConfig(selectedSourceId, {
                sdr_distribution_enabled: sdrEnabled,
                sdr_assignments: assignments,
                fallback_sdr: fallbackSdr || null,
            });
            setSuccessMessage('Configuração salva com sucesso!');
            setSources(prev => prev.map(s => s.id !== selectedSourceId ? s : {
                ...s,
                ghl_routing: { ...s.ghl_routing, sdr_distribution_enabled: sdrEnabled, sdr_assignments: assignments, fallback_sdr: fallbackSdr || null },
            }));
        } catch (err) {
            await logError(err, { fn: 'FilaRecebimento/save', sourceId: selectedSourceId });
            setErrorMessage(err?.message || 'Erro ao salvar. Tente novamente.');
        } finally {
            setIsSaving(false);
        }
    };

    const selectedSource = sources.find(s => s.id === selectedSourceId);
    const availableSdrs = sdrs.filter(s => !assignments.find(a => a.nome === s.nome));
    const checkinSet = useMemo(() => new Set(sdrs.filter(s => s.status_dia === 1).map(s => s.nome)), [sdrs]);

    const nextSdr = useMemo(() => {
        if (!sdrEnabled) return { kind: 'disabled' };
        const eligible = assignments.filter(a => a.enabled && a.ghl_user_id && checkinSet.has(a.nome));
        if (eligible.length > 0) {
            const slots = buildSlots(eligible);
            const totalSlots = slots.length;
            const prevTotal = queueState?.total_slots ?? 0;
            const slotIdx = prevTotal !== totalSlots ? 0 : ((queueState?.slot_index ?? 0) % totalSlots);
            return { kind: 'round_robin', sdr: slots[slotIdx]?.nome, slotIndex: slotIdx, totalSlots };
        }
        if (fallbackSdr) {
            const fb = assignments.find(a => a.nome === fallbackSdr && a.ghl_user_id);
            if (fb) return { kind: 'fallback', sdr: fb.nome };
        }
        // Fallback global: algum SDR tem check-in, mas não é da fila desta fonte
        if (checkinSet.size > 0) return { kind: 'global_fallback' };
        // Nenhum SDR em check-in → não atribuído
        return { kind: 'no_checkin' };
    }, [sdrEnabled, assignments, checkinSet, fallbackSdr, queueState]);

    const distribution7d = useMemo(() => {
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const recent = history.filter(h => new Date(h.created_at).getTime() >= cutoff);
        const total = recent.length;
        if (total === 0) return { total: 0, byNome: [] };
        const counts = {};
        for (const h of recent) counts[h.assigned_sdr] = (counts[h.assigned_sdr] || 0) + 1;
        return {
            total,
            byNome: Object.entries(counts)
                .map(([nome, count]) => ({ nome, count, pct: Math.round((count / total) * 100) }))
                .sort((a, b) => b.count - a.count),
        };
    }, [history]);

    const sourceEnabledMismatch = selectedSource?.enabled && !sdrEnabled;

    return (
        <>
            {!embedded && (
                <div className="page-header">
                    <div className="page-header-left">
                        <div>
                            <div className="page-kicker">Operações</div>
                            <h1 className="page-title">Fila de Recebimento</h1>
                        </div>
                    </div>
                </div>
            )}

            <div className="page-body" style={embedded ? { minHeight: 'auto' } : undefined}>
                {isLoading ? (
                    <div className="loading-state">Carregando configurações...</div>
                ) : sources.length === 0 ? (
                    <div className="empty-state">Nenhuma fonte configurada no lead-hub ainda.</div>
                ) : (
                    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>

                        {/* Fontes */}
                        <div className="card" style={{ width: '210px', flexShrink: 0, padding: '1rem' }}>
                            <p className="section-label">Fontes</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                                {sources.map(source => {
                                    const active = source.id === selectedSourceId;
                                    return (
                                        <button
                                            key={source.id}
                                            onClick={() => setSelectedSourceId(source.id)}
                                            style={{
                                                textAlign: 'left',
                                                padding: '0.5rem 0.75rem',
                                                borderRadius: '8px',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                background: active ? 'rgba(0, 214, 143, 0.1)' : 'transparent',
                                                color: active ? 'var(--emerald)' : 'var(--ink)',
                                                fontWeight: active ? 600 : 400,
                                                transition: 'all 0.15s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            {source.name}
                                            {source.ghl_routing?.sdr_distribution_enabled && (
                                                <span className="chip chip-active" style={{ fontSize: '0.6rem', padding: '1px 5px' }}>ATIVO</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Painel de config */}
                        {selectedSource && (
                            <div style={{ flex: 1, minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                {successMessage && <div className="alert alert-success">{successMessage}</div>}
                                {errorMessage && <div className="alert alert-error">{errorMessage}</div>}

                                {sourceEnabledMismatch && (
                                    <div className="alert alert-warn">
                                        Fonte ativa no lead-hub mas distribuição automática desligada. Leads novos caem no <strong>fallback global</strong>.
                                    </div>
                                )}

                                {/* Config principal */}
                                <div className="card">
                                    <p className="section-label">{selectedSource.name}</p>

                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', marginBottom: sdrEnabled ? '1.25rem' : 0 }}>
                                        <div
                                            onClick={() => setSdrEnabled(v => !v)}
                                            style={{
                                                width: '40px', height: '22px', borderRadius: '11px', position: 'relative', cursor: 'pointer', flexShrink: 0,
                                                background: sdrEnabled ? 'var(--emerald)' : 'var(--glass-border)',
                                                transition: 'background 0.2s',
                                            }}
                                        >
                                            <div style={{
                                                position: 'absolute', top: '3px', left: sdrEnabled ? '19px' : '3px',
                                                width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                                            }} />
                                        </div>
                                        <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--ink)' }}>
                                            Distribuição automática de SDR
                                        </span>
                                    </label>

                                    {sdrEnabled && (
                                        <>
                                            <p style={{ ...sLabel, marginBottom: '0.625rem' }}>SDRs na fila</p>

                                            {assignments.length === 0 && (
                                                <p style={{ fontSize: '0.8rem', color: 'var(--muted)', fontStyle: 'italic', marginBottom: '0.75rem' }}>
                                                    Nenhum SDR adicionado ainda.
                                                </p>
                                            )}

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                                {assignments.map(a => {
                                                    const pct = totalWeight > 0 ? Math.round((a.weight / totalWeight) * 100) : 0;
                                                    return (
                                                        <div key={a.nome} style={{
                                                            display: 'flex', alignItems: 'center', gap: '0.625rem',
                                                            padding: '0.625rem 0.875rem',
                                                            borderRadius: '8px',
                                                            border: '1px solid var(--glass-border)',
                                                            background: a.enabled ? 'var(--canvas)' : 'transparent',
                                                            opacity: a.enabled ? 1 : 0.5,
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={a.enabled}
                                                                onChange={e => updateAssignment(a.nome, 'enabled', e.target.checked)}
                                                                style={{ accentColor: 'var(--emerald)', flexShrink: 0 }}
                                                                title="Incluir na fila"
                                                            />
                                                            <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--ink)' }}>
                                                                {a.nome}
                                                                {checkinSet.has(a.nome)
                                                                    ? <span className="chip chip-active" style={{ fontSize: '0.6rem', padding: '1px 5px' }}>CHECK-IN</span>
                                                                    : <span className="chip chip-muted" style={{ fontSize: '0.6rem', padding: '1px 5px' }}>FORA</span>
                                                                }
                                                            </span>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                                                <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>Peso</span>
                                                                <input
                                                                    type="number" min="1" max="100" value={a.weight}
                                                                    onChange={e => updateAssignment(a.nome, 'weight', Number(e.target.value))}
                                                                    style={{
                                                                        width: '50px', padding: '0.25rem 0.375rem', borderRadius: '6px',
                                                                        border: '1px solid var(--glass-border)', background: 'var(--canvas-2)',
                                                                        color: 'var(--ink)', fontSize: '0.8rem', textAlign: 'center',
                                                                    }}
                                                                />
                                                                {a.enabled && (
                                                                    <span style={{ fontSize: '0.7rem', color: 'var(--emerald)', minWidth: '30px' }}>{pct}%</span>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={() => removeSdr(a.nome)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger-soft)', fontSize: '1rem', padding: '0 2px' }}
                                                                title="Remover"
                                                            >×</button>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {availableSdrs.length > 0 && (
                                                <select
                                                    defaultValue=""
                                                    onChange={e => { if (e.target.value) { addSdr(e.target.value); e.target.value = ''; } }}
                                                    style={{
                                                        width: '100%', padding: '0.4rem 0.625rem', borderRadius: '8px', marginBottom: '1.25rem',
                                                        border: '1px dashed var(--glass-border)', background: 'transparent',
                                                        color: 'var(--muted)', fontSize: '0.8rem', cursor: 'pointer',
                                                    }}
                                                >
                                                    <option value="">+ Adicionar SDR à fila...</option>
                                                    {availableSdrs.map(s => <option key={s.nome} value={s.nome}>{s.nome}</option>)}
                                                </select>
                                            )}

                                            <div style={{ marginBottom: '1.25rem' }}>
                                                <label style={{ ...sLabel, display: 'block', marginBottom: '0.375rem' }}>
                                                    SDR padrão (quando nenhum está em check-in)
                                                </label>
                                                <select
                                                    value={fallbackSdr}
                                                    onChange={e => setFallbackSdr(e.target.value)}
                                                    style={{
                                                        width: '100%', padding: '0.5rem 0.625rem', borderRadius: '8px',
                                                        border: '1px solid var(--glass-border)', background: 'var(--canvas-2)',
                                                        color: 'var(--ink)', fontSize: '0.875rem',
                                                    }}
                                                >
                                                    <option value="">Sem SDR padrão</option>
                                                    {assignments.map(a => <option key={a.nome} value={a.nome}>{a.nome}</option>)}
                                                </select>
                                            </div>
                                        </>
                                    )}

                                    <div className="form-footer">
                                        <button className="pill pill-primary" onClick={handleSave} disabled={isSaving}>
                                            {isSaving ? 'Salvando...' : 'Salvar configuração'}
                                        </button>
                                    </div>
                                </div>

                                {/* Observabilidade */}
                                <div className="card">
                                    <p className="section-label">Estado da fila</p>

                                    <div className="grid-2" style={{ marginBottom: '1.25rem' }}>
                                        <div style={{ padding: '0.75rem', background: 'var(--canvas)', border: '1px solid var(--glass-border)', borderRadius: '8px' }}>
                                            <p style={sLabel}>Próximo SDR</p>
                                            {nextSdr.kind === 'disabled' && <p style={{ fontSize: '0.825rem', color: 'var(--muted)', fontStyle: 'italic' }}>Distribuição desligada</p>}
                                            {nextSdr.kind === 'round_robin' && (
                                                <>
                                                    <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--emerald)' }}>{nextSdr.sdr || '—'}</p>
                                                    <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.125rem' }}>slot {nextSdr.slotIndex + 1} de {nextSdr.totalSlots}</p>
                                                </>
                                            )}
                                            {nextSdr.kind === 'fallback' && (
                                                <>
                                                    <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--warn-soft)' }}>{nextSdr.sdr}</p>
                                                    <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.125rem' }}>fallback da fonte</p>
                                                </>
                                            )}
                                            {nextSdr.kind === 'global_fallback' && (
                                                <>
                                                    <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--warn-soft)' }}>Fallback global</p>
                                                    <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.125rem' }}>round-robin entre SDRs com check-in</p>
                                                </>
                                            )}
                                            {nextSdr.kind === 'no_checkin' && (
                                                <>
                                                    <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--muted)' }}>Não atribuído</p>
                                                    <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.125rem' }}>nenhum SDR em check-in</p>
                                                </>
                                            )}
                                        </div>
                                        <div style={{ padding: '0.75rem', background: 'var(--canvas)', border: '1px solid var(--glass-border)', borderRadius: '8px' }}>
                                            <p style={sLabel}>Última atribuição</p>
                                            {queueState?.last_assigned_sdr ? (
                                                <>
                                                    <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--ink)' }}>{queueState.last_assigned_sdr}</p>
                                                    <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.125rem' }}>{formatDateTime(queueState.last_assigned_at)}</p>
                                                </>
                                            ) : (
                                                <p style={{ fontSize: '0.825rem', color: 'var(--muted)', fontStyle: 'italic' }}>Nenhuma atribuição ainda</p>
                                            )}
                                        </div>
                                    </div>

                                    <p style={{ ...sLabel, marginBottom: '0.5rem' }}>
                                        Distribuição — últimos 7 dias ({distribution7d.total} leads)
                                    </p>
                                    {distribution7d.byNome.length === 0 ? (
                                        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', fontStyle: 'italic', marginBottom: '1.25rem' }}>Sem dados nos últimos 7 dias.</p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '1.25rem' }}>
                                            {distribution7d.byNome.map(d => (
                                                <div key={d.nome} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                                                    <span style={{ flex: '0 0 110px', fontWeight: 500, color: 'var(--ink)' }}>{d.nome}</span>
                                                    <div style={{ flex: 1, height: '5px', background: 'rgba(240, 245, 242, 0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${d.pct}%`, height: '100%', background: 'var(--emerald)' }} />
                                                    </div>
                                                    <span style={{ flex: '0 0 60px', textAlign: 'right', color: 'var(--muted)', fontSize: '0.75rem' }}>{d.count} ({d.pct}%)</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <p style={{ ...sLabel, marginBottom: '0.5rem' }}>Últimas atribuições</p>
                                    {obsLoading ? (
                                        <p style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>Carregando histórico...</p>
                                    ) : history.length === 0 ? (
                                        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', fontStyle: 'italic' }}>Sem atribuições para esta fonte.</p>
                                    ) : (
                                        <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid var(--glass-border)', borderRadius: '8px' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                                <thead style={{ position: 'sticky', top: 0, background: 'var(--canvas-2)' }}>
                                                    <tr>
                                                        <th style={{ padding: '0.5rem 0.625rem', fontWeight: 600, textAlign: 'left', color: 'var(--muted)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Quando</th>
                                                        <th style={{ padding: '0.5rem 0.625rem', fontWeight: 600, textAlign: 'left', color: 'var(--muted)', fontSize: '0.7rem', textTransform: 'uppercase' }}>SDR</th>
                                                        <th style={{ padding: '0.5rem 0.625rem', fontWeight: 600, textAlign: 'left', color: 'var(--muted)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Estratégia</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {history.map(h => (
                                                        <tr key={h.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                                                            <td style={{ padding: '0.4rem 0.625rem', color: 'var(--muted)' }}>{formatDateTime(h.created_at)}</td>
                                                            <td style={{ padding: '0.4rem 0.625rem', fontWeight: 500, color: 'var(--ink)' }}>{h.assigned_sdr || '—'}</td>
                                                            <td style={{ padding: '0.4rem 0.625rem', fontSize: '0.7rem', color: 'var(--muted)' }}>{STRATEGY_LABEL[h.strategy] || h.strategy}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default FilaRecebimento;
